import React, { forwardRef, useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { keyPress } from '../../utils/key-press-utility';

import NavbarItem from './navbar-item';

const keyConstants = {
    BACKSPACE: 'Backspace',
    DEL: 'Delete',
    ESCAPE: 'Escape',
    TAB: 'Tab',
};

const Dropdown = forwardRef((props, ref) => {
    const [isActive, setIsActive] = useState(false);
    const [isTouchDevice, setisTouchDevice] = useState(props.isTouchDevice);
    const [offsetDropdown, setOffsetDropdown] = useState(props.offsetDropdown);
    const firstUpdate = useRef(true);

    const dropdownHandler = (e, clickHandler = () => {}, isButton, touchEvent = false) => {
        if (touchEvent || isButton) {
            const newActiveState = !isActive;
            if (touchEvent && !isSubmenuItem) { // change body only on top level dropdowns
                document.body.style.overflow = newActiveState ? 'hidden' : 'unset';
            }
            setIsActive(newActiveState);
            clickHandler(e, newActiveState);
        }
    };
    // close on outside clicks
    const outsideClickHandler = useCallback((e) => {
        const isOutsideElem = !ref.current.contains(e.target) && ref.current !== e.target;
        if (isActive && isOutsideElem) {
            setIsActive(false);
            document.body.style.overflow = 'unset';
        }
    });
    // close on key press
    const keyPressHandler = useCallback(() => {
        ref.current.firstChild.focus();
        setIsActive(false);
        document.body.style.overflow = 'unset';
    });
    // close when transitioning between small and large screens
    const handleScreenTransition = useCallback(() => {
        if (!firstUpdate.current) {
            setOffsetDropdown(props.offsetDropdown);
            setIsActive(false);
            document.body.style.overflow = 'unset';
        }
        firstUpdate.current = false;
    });

    const {
        BACKSPACE,
        DEL,
        ESCAPE,
    } = keyConstants;

    const { downHandler } = keyPress([BACKSPACE, DEL, ESCAPE], isActive, keyPressHandler);

    useEffect(() => {
        document.addEventListener('click', outsideClickHandler, false);
        window.addEventListener('keydown', downHandler);

        return () => {
            document.removeEventListener('click', outsideClickHandler);
            window.removeEventListener('keydown', downHandler);
        }
    }, [outsideClickHandler, keyPressHandler]);

    useEffect(() => {
        if (!firstUpdate.current && isTouchDevice !== props.isTouchDevice) {
            setisTouchDevice(props.isTouchDevice);
        }
    }, [props.isTouchDevice]);

    useEffect(() => {
        if (props.offsetDropdown !== offsetDropdown) {
            setOffsetDropdown(props.offsetDropdown);
        }
    }, [props.offsetDropdown]);

    // after initial render close subnav when tranisitioning between desktop -> mobile
    useEffect(handleScreenTransition, [props.isSmallScreen]);

    const {
            buttonData: { ariaLabel = '', text = '' } = {},
            className = 'navbar__dropdown',
            clickHandler,
            isSubmenuItem = false,
            items = [],
            itemData: { item = {}, path = '' } = {},
            isButton = false,
            isSmallScreen,
        }
    = props;

    // TODO : Fix compilation process and CSS import order so we don't need inline-styles
    const dropdownStyle = {
        height: (isSubmenuItem || !isTouchDevice && !isSmallScreen) ? 'auto' : `calc(100vh - ${offsetDropdown}px)`,
        maxHeight: !isSubmenuItem && `calc(100vh - ${offsetDropdown}px)`
    };

    const containerStyle = {
        display: isSubmenuItem ? 'block' : 'inline-flex'
    };

    return (
        <div 
            aria-haspopup='true' 
            aria-expanded={isActive}
            className={classNames(className, {
                'navbar__dropdown--active': isActive
            })}
            ref={ref}
            style={containerStyle}
        >
            {isButton ? 
                <button
                    className='navbar__dropdown-btn'
                    aria-label={ariaLabel}
                    aria-haspopup='true'
                    onClick={(e) => { dropdownHandler(e, clickHandler, isButton, isTouchDevice, isSubmenuItem) }}
                >
                    {text}
                </button>
            :
                <NavbarItem
                    item={item}
                    itemsBasePath={path}
                    isLink={!isTouchDevice}
                    clickHandler={(e) => dropdownHandler(e, clickHandler, isButton, isTouchDevice, isSubmenuItem)}
                />
            }
            <ul className={classNames({
                    'navbar__dropdown-items': !isSubmenuItem,
                    'navbar__dropdown-columns': true,
                    'navbar__dropdown-items--submenu': isSubmenuItem,

                })} 
                style={dropdownStyle}
            >
                {items}
                { (isTouchDevice && !isSubmenuItem) && <li className="scroll-buffer"></li> }
            </ul>
        </div>
    );
});

Dropdown.propTypes = {
    buttonData: PropTypes.shape({
        ariaLabel: PropTypes.string,
        text: PropTypes.string
    }),
    className: PropTypes.any,
    clickHandler: PropTypes.func,
    isSubmenuItem: PropTypes.bool,
    isSmallScreen: PropTypes.bool,
    isTouchDevice: PropTypes.bool,
    isButton: PropTypes.bool,
    items: PropTypes.node,
    itemData: PropTypes.shape({
        item: PropTypes.any,
        path: PropTypes.string
    }),
    offsetDropdown: PropTypes.number
};

export default Dropdown;
